import { useState } from "react";
import { Box, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { DeleteSweep, Info, CheckCircle } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import PortalService from "../../../../../services/supabase/PortalService";

interface DummyProductsBannerProps {
    hasDummyProducts: boolean;
    onDeleted: () => void;
}

export const DummyProductsBanner = ({ hasDummyProducts, onDeleted }: DummyProductsBannerProps) => {
    const theme = useTheme();
    const [isDeleting, setIsDeleting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const portalService = PortalService();

    if (!hasDummyProducts) {
        return null;
    }

    const handleDeleteDummyProducts = async () => {
        if (!confirm("Are you sure you want to delete all dummy/sample products? This action cannot be undone.")) {
            return;
        }

        setIsDeleting(true);
        setError(null);

        try {
            const result = await portalService.deleteDummyProducts();
            
            if (result.statusCode === 200) {
                setShowSuccess(true);
                setTimeout(() => {
                    onDeleted();
                }, 2000);
            } else {
                setError(result.errorMessage || "Failed to delete dummy products");
            }
        } catch (err) {
            console.error("Error deleting dummy products:", err);
            setError("An unexpected error occurred");
        } finally {
            setIsDeleting(false);
        }
    };

    if (showSuccess) {
        return (
            <Alert
                severity="success"
                icon={<CheckCircle />}
                sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": {
                        fontSize: "2rem",
                    },
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Dummy Products Deleted
                </Typography>
                <Typography variant="body2">
                    All sample products have been successfully removed from your store.
                </Typography>
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 193, 7, 0.05)'
                    : 'rgba(255, 193, 7, 0.08)',
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)'}`,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Info
                        sx={{
                            color: theme.palette.mode === 'dark' ? '#fbc02d' : '#f57f17',
                            fontSize: '1.25rem',
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                        }}
                    >
                        Sample products detected. Ready to use your own?
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteSweep />}
                    onClick={handleDeleteDummyProducts}
                    disabled={isDeleting}
                    sx={{
                        textTransform: 'none',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 193, 7, 0.6)',
                        color: theme.palette.mode === 'dark' ? '#fbc02d' : '#f57f17',
                        '&:hover': {
                            borderColor: theme.palette.mode === 'dark' ? '#fbc02d' : '#f57f17',
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 193, 7, 0.1)'
                                : 'rgba(255, 193, 7, 0.15)',
                        },
                    }}
                >
                    {isDeleting ? 'Deleting...' : 'Remove Samples'}
                </Button>
            </Box>
        </Box>
    );
};

